import { useState, useMemo, useCallback, type ReactNode, type Dispatch, type SetStateAction } from 'react';
import { ScanLine } from 'lucide-react';
import { rpc } from '@/api/client';
import { useToast } from '@/context/ToastContext';
import { useI18n } from '@/context/I18nContext';
import { BarcodeScanner } from '@/components/tyre/BarcodeScanner';
import type { LookupTyreByBarcodeResponse } from '@/types/telemetry';

// ─── Types ──────────────────────────────────────────────

export interface SetupData {
  chassisId?: string;
  engineId?: string;
  tyreId?: string;
  carbId?: string;
  axleId?: string;
  sparkPlugId?: string;
  flOut?: string; frOut?: string; rlOut?: string; rrOut?: string;
  flIn?: string; frIn?: string; rlIn?: string; rrIn?: string;
  rearWidth?: string;
  frontHubId?: string;
  rearHubId?: string;
  loosenedRearBumper?: boolean | string;
  frontCaster?: string;
  frontCamber?: string;
  frontTrackWidth?: string;
  frontRideHeight?: string;
  frontAckermann?: string;
  frontToe?: string;
  frontTorsionBar?: string;
  frontSprocket?: string;
  rearSprocket?: string;
  carbMainJet?: string;
  carbIdleJet?: string;
  carbNeedleClip?: string;
  carbAirScrew?: string;
  carbFloatHeight?: string;
  weight?: string;
  notes?: string;
}

export interface InventoryItem {
  id?: string;
  brand?: string;
  type?: string;
  nr?: string;
  stiffness?: string;
  position?: string;
  width?: string;
  [key: string]: unknown;
}

export interface SetupInventory {
  chassis: InventoryItem[];
  engines: InventoryItem[];
  tyres: InventoryItem[];
  carburetors: InventoryItem[];
  axles: InventoryItem[];
  hubs: InventoryItem[];
  sparkPlugs: InventoryItem[];
}

export const SETUP_KEYS = [
  'chassisId', 'engineId', 'tyreId', 'carbId', 'axleId', 'sparkPlugId',
  'flOut', 'frOut', 'rlOut', 'rrOut', 'flIn', 'frIn', 'rlIn', 'rrIn',
  'rearWidth', 'frontHubId', 'rearHubId', 'frontCaster', 'frontCamber',
  'frontTrackWidth', 'frontRideHeight', 'frontAckermann', 'frontToe',
  'frontTorsionBar', 'frontSprocket', 'rearSprocket', 'carbMainJet',
  'carbIdleJet', 'carbNeedleClip', 'carbAirScrew', 'carbFloatHeight', 'weight', 'notes',
] as const;

export function isSetupEmpty(data: SetupData): boolean {
  for (const k of SETUP_KEYS) {
    const v = data[k];
    if (v !== undefined && v !== null && v !== '' && v !== false) return false;
  }
  if (data.loosenedRearBumper === true || data.loosenedRearBumper === 'true') return false;
  return true;
}

// ─── Helpers ────────────────────────────────────────────

function selectOptions(items: InventoryItem[], noneLabel: string): { value: string; label: string }[] {
  return [
    { value: '', label: noneLabel },
    ...(items || []).map(item => ({
      value: String(item.id ?? ''),
      label: `${item.brand ?? ''} ${item.type ?? ''}${item.nr ? ' - #' + item.nr : ''}`.trim(),
    })),
  ];
}

function axleOptions(items: InventoryItem[], noneLabel: string) {
  return [
    { value: '', label: noneLabel },
    ...(items || []).map(a => ({
      value: String(a.id ?? ''),
      label: `${a.brand ?? ''} ${a.type ?? ''} (${a.stiffness ?? ''})`.trim(),
    })),
  ];
}

function hubOptions(items: InventoryItem[], position: string, noneLabel: string) {
  return [
    { value: '', label: noneLabel },
    ...(items || [])
      .filter(h => String(h.position ?? '').toLowerCase() === position)
      .map(h => ({
        value: String(h.id ?? ''),
        label: `${h.brand ?? ''} ${h.type ?? ''}${h.width ? ' (' + h.width + ')' : ''}`.trim(),
      })),
  ];
}

// ─── Component ──────────────────────────────────────────

interface SetupFormProps {
  data: SetupData;
  inventory: SetupInventory;
  readOnly: boolean;
  onChange: (partial: Partial<SetupData>) => void;
  onInventoryUpdate?: Dispatch<SetStateAction<SetupInventory | null>>;
}

export function SetupForm({
  data,
  inventory,
  readOnly,
  onChange,
  onInventoryUpdate,
}: SetupFormProps) {
  const { t } = useI18n();
  const { showToast } = useToast();
  const [showScanner, setShowScanner] = useState(false);

  const noneLabel = t('common.none');
  const notSetLabel = t('common.notSet');

  const tyreOpts = useMemo(() => selectOptions(inventory.tyres, noneLabel), [inventory.tyres, noneLabel]);
  const chassisOpts = useMemo(() => selectOptions(inventory.chassis, noneLabel), [inventory.chassis, noneLabel]);
  const engineOpts = useMemo(() => selectOptions(inventory.engines, noneLabel), [inventory.engines, noneLabel]);
  const sparkPlugOpts = useMemo(() => selectOptions(inventory.sparkPlugs, noneLabel), [inventory.sparkPlugs, noneLabel]);
  const carbOpts = useMemo(() => selectOptions(inventory.carburetors, noneLabel), [inventory.carburetors, noneLabel]);
  const axleOpts = useMemo(() => axleOptions(inventory.axles, noneLabel), [inventory.axles, noneLabel]);
  const frontHubOpts = useMemo(() => hubOptions(inventory.hubs, 'front', noneLabel), [inventory.hubs, noneLabel]);
  const rearHubOpts = useMemo(() => hubOptions(inventory.hubs, 'rear', noneLabel), [inventory.hubs, noneLabel]);

  const gearRatio = useMemo(() => {
    const front = parseFloat(data.frontSprocket ?? '');
    const rear = parseFloat(data.rearSprocket ?? '');
    if (front > 0 && rear > 0) return (rear / front).toFixed(3);
    return null;
  }, [data.frontSprocket, data.rearSprocket]);

  const handleScanSuccess = useCallback(async (barcode: string) => {
    setShowScanner(false);
    const bc = barcode.trim();
    if (!bc) return;

    const bcLower = bc.toLowerCase();
    // 1. Try local inventory match
    const localMatch = inventory.tyres.find(tyre => {
      if (tyre.id?.toLowerCase() === bcLower) return true;
      if (tyre.nr?.toLowerCase() === bcLower) return true;
      const tyreIdKeys = ['tyre_id_fl', 'tyre_id_fr', 'tyre_id_rl', 'tyre_id_rr', 'tyreIdFl', 'tyreIdFr', 'tyreIdRl', 'tyreIdRr'];
      return tyreIdKeys.some(k => {
        const val = (tyre as Record<string, unknown>)[k];
        return typeof val === 'string' && val.trim().toLowerCase() === bcLower;
      });
    });

    if (localMatch && localMatch.id) {
      onChange({ tyreId: localMatch.id });
      showToast(t('common.success'), 'success');
      return;
    }

    // 2. Query backend by barcode
    try {
      const resp = await rpc<LookupTyreByBarcodeResponse>('LookupTyreByBarcode', { barcode: bc });
      const tyres = resp.tyres || [];
      if (tyres.length > 0) {
        const found = tyres[0];
        onInventoryUpdate?.(prev => {
          if (!prev) return prev;
          if (prev.tyres.some(tyre => tyre.id === found.id)) return prev;
          return {
            ...prev,
            tyres: [
              ...prev.tyres,
              {
                id: found.id,
                brand: found.brand,
                type: found.type,
                nr: found.nr,
                compound: found.compound,
                tyre_id_fl: found.tyreIdFl,
                tyre_id_fr: found.tyreIdFr,
                tyre_id_rl: found.tyreIdRl,
                tyre_id_rr: found.tyreIdRr,
              },
            ],
          };
        });
        onChange({ tyreId: found.id });
        showToast(t('common.success'), 'success');
        return;
      }
    } catch {
      // ignore rpc failure and fall through
    }

    showToast(t('tyreOverview.scanNotFound'), 'error');
  }, [inventory.tyres, onChange, onInventoryUpdate, showToast, t]);

  return (
    <>
      <fieldset disabled={readOnly} className="border-0 p-0 m-0 min-w-0">
        <div className="flex flex-col gap-8">
          {/* ── 1. Tyre Pressures ──────────────────────────── */}
          <Section title={t('pressure.tyre')}>
            <div className="space-y-6">
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-2">{t('pressure.tyre')}</label>
                <div className="flex items-center gap-2">
                  <select
                    value={data.tyreId ?? ''}
                    onChange={e => onChange({ tyreId: e.target.value })}
                    className="editor-input editor-select flex-1 sm:max-w-[320px]"
                  >
                    {tyreOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <button
                    type="button"
                    disabled={readOnly}
                    onClick={() => setShowScanner(true)}
                    className="p-2 rounded-md border border-border-default text-text-secondary
                      hover:text-text-primary hover:border-border-hover transition-colors cursor-pointer
                      shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={t('tyre.scanBarcode')}
                    aria-label={t('tyre.scanBarcode')}
                  >
                    <ScanLine size={16} />
                  </button>
                </div>
              </div>

              <PressureGrid
                title={t('pressure.outgoing')}
                subtitle={t('pressure.cold')}
                fl={data.flOut} fr={data.frOut} rl={data.rlOut} rr={data.rrOut}
                labelFl={t('pressure.fl')} labelFr={t('pressure.fr')}
                labelRl={t('pressure.rl')} labelRr={t('pressure.rr')}
                onChange={(k, v) => onChange({ [k]: v } as Partial<SetupData>)}
                keys={{ fl: 'flOut', fr: 'frOut', rl: 'rlOut', rr: 'rrOut' }}
              />

              <PressureGrid
                title={t('pressure.incoming')}
                subtitle={t('pressure.hot')}
                fl={data.flIn} fr={data.frIn} rl={data.rlIn} rr={data.rrIn}
                labelFl={t('pressure.fl')} labelFr={t('pressure.fr')}
                labelRl={t('pressure.rl')} labelRr={t('pressure.rr')}
                onChange={(k, v) => onChange({ [k]: v } as Partial<SetupData>)}
                keys={{ fl: 'flIn', fr: 'frIn', rl: 'rlIn', rr: 'rrIn' }}
              />
            </div>
          </Section>

          {/* ── 2. Front Geometry ──────────────────────────── */}
          <Section title={t('setup.tabFront')}>
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label={t('front.caster')} unit="mm">
                  <input type="text" value={data.frontCaster ?? ''} onChange={e => onChange({ frontCaster: e.target.value })} className="editor-input" placeholder="—" />
                </Field>
                <Field label={t('front.camber')} unit="mm">
                  <input type="text" value={data.frontCamber ?? ''} onChange={e => onChange({ frontCamber: e.target.value })} className="editor-input" placeholder="—" />
                </Field>
                <Field label={t('front.trackWidth')} unit={t('front.trackWidthUnit')}>
                  <input type="text" value={data.frontTrackWidth ?? ''} onChange={e => onChange({ frontTrackWidth: e.target.value })} className="editor-input" placeholder="—" />
                </Field>
                <Field label={t('front.rideHeight')} unit="mm">
                  <input type="text" value={data.frontRideHeight ?? ''} onChange={e => onChange({ frontRideHeight: e.target.value })} className="editor-input" placeholder="—" />
                </Field>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-5">
                <Field label={t('front.frontHubs')}>
                  <select value={data.frontHubId ?? ''} onChange={e => onChange({ frontHubId: e.target.value })} className="editor-input editor-select">
                    {frontHubOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </Field>
                <Field label={t('front.ackermann')}>
                  <select value={data.frontAckermann ?? ''} onChange={e => onChange({ frontAckermann: e.target.value })} className="editor-input editor-select">
                    <option value="">{notSetLabel}</option>
                    <option value="inner">{t('front.inner')}</option>
                    <option value="outer">{t('front.outer')}</option>
                  </select>
                </Field>
                <Field label={t('front.toeAlignment')} unit="mm">
                  <input type="text" value={data.frontToe ?? ''} onChange={e => onChange({ frontToe: e.target.value })} className="editor-input" placeholder="—" />
                </Field>
                <Field label={t('front.torsionBar')}>
                  <select value={data.frontTorsionBar ?? ''} onChange={e => onChange({ frontTorsionBar: e.target.value })} className="editor-input editor-select">
                    <option value="">{notSetLabel}</option>
                    <option value="hard">{t('front.hard')}</option>
                    <option value="medium">{t('front.medium')}</option>
                    <option value="soft">{t('front.soft')}</option>
                    <option value="none">{noneLabel}</option>
                  </select>
                </Field>
              </div>
            </div>
          </Section>

          {/* ── 3. Rear Config ─────────────────────────────── */}
          <Section title={t('setup.tabRear')}>
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <Field label={t('rear.axle')}>
                  <select value={data.axleId ?? ''} onChange={e => onChange({ axleId: e.target.value })} className="editor-input editor-select">
                    {axleOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </Field>
                <Field label={t('rear.rearWidth')} unit="mm">
                  <input type="text" value={data.rearWidth ?? ''} onChange={e => onChange({ rearWidth: e.target.value })} className="editor-input" placeholder="—" />
                </Field>
                <Field label={t('rear.rearHubs')}>
                  <select value={data.rearHubId ?? ''} onChange={e => onChange({ rearHubId: e.target.value })} className="editor-input editor-select">
                    {rearHubOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </Field>
              </div>
              <div>
                <label className="checkbox-toggle">
                  <input
                    type="checkbox"
                    checked={data.loosenedRearBumper === true || data.loosenedRearBumper === 'true'}
                    onChange={e => onChange({ loosenedRearBumper: e.target.checked })}
                  />
                  <span className="checkbox-toggle__track" />
                  <span className="checkbox-toggle__label">{t('rear.loosenedBumper')}</span>
                </label>
              </div>
            </div>
          </Section>

          {/* ── 4. Engine & Gearing ────────────────────────── */}
          <Section title={t('engine.gearRatio')}>
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:flex sm:items-end gap-5">
                <div className="w-full sm:w-auto sm:min-w-[200px]">
                  <label className="block text-xs font-medium text-text-secondary mb-2">{t('engine.engine')}</label>
                  <select value={data.engineId ?? ''} onChange={e => onChange({ engineId: e.target.value })} className="editor-input editor-select w-full">
                    {engineOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div className="w-full sm:w-auto sm:min-w-[200px]">
                  <label className="block text-xs font-medium text-text-secondary mb-2">{t('engine.sparkPlug')}</label>
                  <select value={data.sparkPlugId ?? ''} onChange={e => onChange({ sparkPlugId: e.target.value })} className="editor-input editor-select w-full">
                    {sparkPlugOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <h4 className="section-title mb-4">{t('engine.gearRatio')}</h4>
                <div className="grid grid-cols-1 sm:flex sm:items-end gap-5">
                  <Field label={t('engine.pinion')} unit="T">
                    <input type="text" value={data.frontSprocket ?? ''} onChange={e => onChange({ frontSprocket: e.target.value })} className="editor-input" placeholder="—" />
                  </Field>
                  <Field label={t('engine.crown')} unit="T">
                    <input type="text" value={data.rearSprocket ?? ''} onChange={e => onChange({ rearSprocket: e.target.value })} className="editor-input" placeholder="—" />
                  </Field>
                  {gearRatio && (
                    <div className="ratio-display pb-1">
                      = {gearRatio}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Section>

          {/* ── 5. Carburetor ──────────────────────────────── */}
          <Section title={t('carb.carburetor')}>
            <div className="space-y-6">
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-2">{t('carb.carburetor')}</label>
                <select value={data.carbId ?? ''} onChange={e => onChange({ carbId: e.target.value })} className="editor-input editor-select w-full sm:max-w-[320px]">
                  {carbOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <Field label={t('carb.mainJet')}>
                  <input type="text" value={data.carbMainJet ?? ''} onChange={e => onChange({ carbMainJet: e.target.value })} className="editor-input" placeholder="—" />
                </Field>
                <Field label={t('carb.idleJet')}>
                  <input type="text" value={data.carbIdleJet ?? ''} onChange={e => onChange({ carbIdleJet: e.target.value })} className="editor-input" placeholder="—" />
                </Field>
                <Field label={t('carb.needleClip')}>
                  <input type="text" value={data.carbNeedleClip ?? ''} onChange={e => onChange({ carbNeedleClip: e.target.value })} className="editor-input" placeholder="—" />
                </Field>
                <Field label={t('carb.airScrew')} unit="turns">
                  <input type="text" value={data.carbAirScrew ?? ''} onChange={e => onChange({ carbAirScrew: e.target.value })} className="editor-input" placeholder="—" />
                </Field>
                <Field label={t('carb.floatHeight')} unit="mm">
                  <input type="text" value={data.carbFloatHeight ?? ''} onChange={e => onChange({ carbFloatHeight: e.target.value })} className="editor-input" placeholder="—" />
                </Field>
              </div>
            </div>
          </Section>

          {/* ── 6. Chassis ─────────────────────────────────── */}
          <Section title={t('chassisSelect.chassis')}>
            <div className="space-y-6">
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-2">{t('chassisSelect.chassis')}</label>
                <select value={data.chassisId ?? ''} onChange={e => onChange({ chassisId: e.target.value })} className="editor-input editor-select w-full sm:max-w-[320px]">
                  {chassisOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <Field label={t('chassisSelect.weight')} unit="kg">
                  <input type="text" value={data.weight ?? ''} onChange={e => onChange({ weight: e.target.value })} className="editor-input" placeholder="—" />
                </Field>
              </div>
            </div>
          </Section>

          {/* ── 7. Notes ───────────────────────────────────── */}
          <Section title={t('notes.notes')}>
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-2">{t('notes.notes')}</label>
              <textarea
                value={data.notes ?? ''}
                onChange={e => onChange({ notes: e.target.value })}
                placeholder={t('notes.placeholder')}
                rows={8}
                className="editor-input editor-textarea"
              />
            </div>
          </Section>
        </div>
      </fieldset>

      {showScanner && (
        <BarcodeScanner
          onScanSuccess={handleScanSuccess}
          onClose={() => setShowScanner(false)}
        />
      )}
    </>
  );
}

// ─── Sub-components ─────────────────────────────────────

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <h3 className="section-title mb-4">{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, unit, children }: { label: string; unit?: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <label className="text-xs font-medium text-text-secondary">
        {label}
        {unit && <span className="font-normal text-text-muted ml-1">{unit}</span>}
      </label>
      {children}
    </div>
  );
}

interface PressureGridProps {
  title: string;
  subtitle: string;
  fl?: string; fr?: string; rl?: string; rr?: string;
  labelFl: string; labelFr: string; labelRl: string; labelRr: string;
  onChange: (key: string, value: string) => void;
  keys: { fl: string; fr: string; rl: string; rr: string };
}

function PressureGrid({ title, subtitle, fl, fr, rl, rr, labelFl, labelFr, labelRl, labelRr, onChange, keys }: PressureGridProps) {
  return (
    <div
      className="border border-border-default rounded-lg p-4 sm:p-5"
      style={{ background: 'var(--color-bg-surface)' }}
    >
      <label className="block text-xs font-medium text-text-secondary mb-3">
        {title} <span className="text-text-muted font-normal">({subtitle})</span>
      </label>
      <div
        className="kart-pressure-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          gridTemplateRows: 'auto auto auto',
          gap: '8px 12px',
          alignItems: 'center',
          maxWidth: '380px',
          margin: '0 auto',
        }}
      >
        <PressureInput label={labelFl} value={fl} onChange={v => onChange(keys.fl, v)} />
        <div />
        <PressureInput label={labelFr} value={fr} onChange={v => onChange(keys.fr, v)} />

        <div />
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 0' }}>
          <KartSilhouette />
        </div>
        <div />

        <PressureInput label={labelRl} value={rl} onChange={v => onChange(keys.rl, v)} />
        <div />
        <PressureInput label={labelRr} value={rr} onChange={v => onChange(keys.rr, v)} />
      </div>
    </div>
  );
}

function PressureInput({ label, value, onChange }: { label: string; value?: string; onChange: (v: string) => void }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <span className="text-[0.68rem] font-semibold uppercase tracking-wider text-text-muted">{label}</span>
      <input
        type="text"
        value={value ?? ''}
        onChange={e => onChange(e.target.value)}
        className="editor-input text-center"
        style={{ maxWidth: '100px' }}
        placeholder="—"
      />
    </div>
  );
}

function KartSilhouette() {
  const w = 80;
  const h = 120;
  const bodyColor = '#ffffff';
  const wheelColor = '#ffffff';

  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ opacity: 0.85 }}
    >
      <rect x={18} y={16} width={44} height={88} rx={10} ry={10} stroke={bodyColor} strokeWidth={1.5} fill="none" />
      <line x1={10} y1={28} x2={70} y2={28} stroke={bodyColor} strokeWidth={1} />
      <line x1={10} y1={92} x2={70} y2={92} stroke={bodyColor} strokeWidth={1} />
      <circle cx={40} cy={36} r={6} stroke={bodyColor} strokeWidth={1} fill="none" />
      <rect x={30} y={52} width={20} height={24} rx={4} ry={4} stroke={bodyColor} strokeWidth={1} fill="none" />
      <path d="M40 6 L36 14 L44 14 Z" fill={bodyColor} opacity={0.6} />
      <rect x={2} y={20} width={10} height={16} rx={3} fill={wheelColor} opacity={0.7} />
      <rect x={68} y={20} width={10} height={16} rx={3} fill={wheelColor} opacity={0.7} />
      <rect x={2} y={84} width={10} height={16} rx={3} fill={wheelColor} opacity={0.7} />
      <rect x={68} y={84} width={10} height={16} rx={3} fill={wheelColor} opacity={0.7} />
    </svg>
  );
}
